import { Holiday } from '@/helpers';
import { DateTime } from 'luxon';
import { Model } from 'pinia-orm';
import { Attr, BelongsTo, BelongsToMany, Bool, HasMany, MorphMany, Num, Str } from 'pinia-orm/dist/decorators';
import AssetType from './AssetType';
import Claim from './Claim';
import Comment from './Comment';
import Company from './Company';
import Labour from './Labour';
import Permissions from './Permissions';
import Plant from './Plant';
import PlantCategory from './PlantCategory';
import ProjectGroup from './ProjectGroup';
import ProjectSupplier from './ProjectSupplier';
import Trade from './Trade';
import User from './User';
import VariationOrder from './VariationOrder';
import VariationOrderCategory from './VariationOrderCategory';
import WbsCode from './WbsCode';
/* --- user header --- */
/* --- end user header --- */

// See src/app/Models/Project.php:89
export interface HeadContractSettings {
  contractor_email: string;
  pqs_email: string;
  superintendent_email: string;
  client_emails: string[];
  commentary_lock: number;
  nov_clause: string;
  nov_required: boolean;
  nov_additional_info_required: boolean;
  nov_period: number;
  nov_business_days: boolean;
  variation_submission_business_days: boolean;
  variation_submission_clause: string;
  variation_submission_required: boolean;
  variation_submission_additional_info_required: boolean;
  variation_submission_period: number;
  nod_clause: string;
  nod_required: boolean;
  nod_additional_info_required: boolean;
  nod_period: number;
  nod_business_days: boolean;
  delay_submission_clause: string;
  delay_submission_required: boolean;
  delay_submission_additional_info_required: boolean;
  delay_submission_period: number;
  delay_submission_business_days: boolean;
  cen_raise_required: boolean;
  cen_raise_period: number;
  cen_raise_business_days: boolean;
  client_details: string;
  hired_company_details: string;
  financier_details: string;
  site_details: string;
  holidays: Holiday[];
  claim_clause: string;
  certificate_clause: string;
  final_account_clause: string;
  claim_margin_percent: number;
  apply_tax: boolean;
  tax_percent: number;
  performance_security: 'retention' | 'bank_guarantee';
  claim_retention_percent: number;
  total_retention_percent: number;
  show_ve_items_on_claims: boolean;
  allow_certified_over_budget: boolean;
}

export interface SubcontractSettings {
  receipt_of_claim_clause: string;
  receipt_of_claim_required: boolean;
  receipt_of_claim_additional_info_required: boolean;
  receipt_of_claim_period: number;
  receipt_of_claim_business_days: boolean;
  issue_of_certificate_clause: string;
  issue_of_certificate_required: boolean;
  issue_of_certificate_additional_info_required: boolean;
  issue_of_certificate_period: number;
  issue_of_certificate_business_days: boolean;
  claims_due_on: number;
  enable_sopa_statement: boolean;
  sopa_statement_text: string;
}

export default class Project extends Model {
  static entity = 'App\\Models\\Project';
  // fields
  @Num(0)
  declare id: number;
  @Attr(undefined)
  declare company_id: number;
  @Num(undefined)
  declare project_group_id: number | undefined;
  @Str('')
  declare name: string;
  @Num(0)
  declare labour_standard_shift: number;
  @Num(0)
  declare plant_standard_shift: number;
  @Num(0)
  declare maximum_shift: number;
  @Num(0)
  declare commentary_lock: number;
  @Num(0)
  declare nov_period: number;
  @Bool(false)
  declare boq_locked: boolean;
  @Bool(false)
  declare feature_flag_progress_claims_lite: boolean;
  @Bool(false)
  declare feature_flag_subcontractor_claims_lite: boolean;
  @Str('')
  declare deleted_at: string | undefined;
  @Str('')
  declare shift_start_at: string;
  @Str('')
  declare shift_end_at: string;
  @Str('')
  declare night_shift_start_at: string;
  @Str('')
  declare claim_start: string;
  @Str('')
  declare default_dashboard: string;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  @Str('')
  declare boq_locked_at: string | undefined;
  @Str('')
  declare reference: string | undefined;
  @Str('')
  declare contractor_email: string | undefined;
  @Str('')
  declare contract_start_date: string | undefined;
  @Str('')
  declare contract_end_date: string | undefined;
  @Str('')
  declare pqs_email: string | undefined;
  @Str('')
  declare nov_clause: string | undefined;
  @Str('')
  declare superintendent_email: string | undefined;
  @Str('')
  declare nod_clause: string | undefined;
  @Str('')
  declare description: string | undefined;
  @Str('')
  declare business_role: string | undefined;
  @Str('')
  declare locale: string | undefined;
  @Str('')
  declare type: 'all' | 'contractor' | 'client';
  @Str(undefined)
  declare archived_at: string;
  @Attr(undefined)
  declare wbs_codes: string[];
  @Attr(undefined)
  declare activities: string[];
  @Attr(undefined)
  declare elements: string[];
  @Attr(undefined)
  declare areas: string[];
  @Bool(false)
  declare track_weekends: boolean;
  @Attr(undefined)
  declare project_settings: unknown;
  @Attr(undefined)
  declare head_contract_settings: HeadContractSettings;
  @Attr(undefined)
  declare subcontract_settings: SubcontractSettings;
  @Attr(undefined)
  declare self_perform_settings: unknown;
  @Num(0)
  declare log_retention_period: number;
  // relations
  @BelongsToMany(() => User, () => Permissions, 'project_id', 'user_id', 'id', 'id')
  declare users: User[];
  @HasMany(() => Plant, 'project_id', 'id')
  declare plants: Plant[];
  @HasMany(() => Labour, 'project_id', 'id')
  declare labours: Labour[];
  @HasMany(() => WbsCode, 'project_id', 'id')
  declare codes: WbsCode[];
  @HasMany(() => VariationOrder, 'project_id', 'id')
  declare orders: VariationOrder[];
  @HasMany(() => VariationOrder, 'project_id', 'id')
  declare variation_orders: VariationOrder[];
  @HasMany(() => ProjectSupplier, 'project_id', 'id')
  declare suppliers: ProjectSupplier[];
  @HasMany(() => AssetType, 'project_id', 'id')
  declare asset_types: AssetType[];
  @HasMany(() => Claim, 'project_id', 'id')
  declare claims: Claim[];
  @HasMany(() => Trade, 'project_id', 'id')
  declare trades: Trade[];
  @BelongsTo(() => Company, 'company_id', 'id')
  declare company: Company;
  @BelongsTo(() => ProjectGroup, 'project_group_id', 'id')
  declare project_group: ProjectGroup;
  @HasMany(() => PlantCategory, 'project_id', 'id')
  declare plant_categories: PlantCategory[];
  @HasMany(() => VariationOrderCategory, 'project_id', 'id')
  declare variation_order_categories: VariationOrderCategory[];
  @MorphMany(() => Comment, 'commentable_id', 'commentable_type', 'id')
  declare comments: Comment[];

  /* --- user code --- */

  get shiftStartAt(): Date {
    const shiftStart = this.shift_start_at.split(':');
    const date = new Date();

    date.setHours(parseInt(shiftStart[0]), parseInt(shiftStart[1]), parseInt(shiftStart[2]));

    return date;
  }

  get nightShiftStartAt(): Date {
    const shiftStart = this.night_shift_start_at.split(':');
    const date = new Date();

    date.setHours(parseInt(shiftStart[0]), parseInt(shiftStart[1]), parseInt(shiftStart[2]));

    return date;
  }

  get contractStartDate(): Date {
    return new Date(this.contract_start_date);
  }

  get contractEndDate(): Date {
    return new Date(this.contract_end_date);
  }

  get contractLength(): number {
    const start = DateTime.fromJSDate(this.contractStartDate).startOf('month');
    const end = DateTime.fromJSDate(this.contractEndDate).endOf('month');

    return Math.ceil(Math.abs(start.diff(end, ['months']).months));
  }

  get contractMonths(): DateTime[] {
    return Array.from({ length: this.contractLength }, (_, i) => {
      const date = DateTime.fromJSDate(this.contractStartDate).startOf('month');

      return date.plus({ months: i });
    });
  }

  get contractLengthInWeeks(): number {
    const start = DateTime.fromJSDate(this.contractStartDate);
    const end = DateTime.fromJSDate(this.contractEndDate);

    return Math.ceil(Math.abs(start.diff(end, ['weeks']).weeks));
  }

  get isClient(): boolean {
    return this.type === 'client';
  }

  get isContractor(): boolean {
    return this.type !== 'client';
  }

  get unarchived(): boolean {
    return !this.archived_at;
  }

  estimateContractEndDate(days: number) {
    return DateTime.fromISO(this.contract_end_date).plus({ days });
  }

  monthsFromContractStart(dateToCheck: DateTime) {
    const start = DateTime.fromJSDate(this.contractStartDate);

    return Math.ceil(Math.abs(start.diff(dateToCheck, 'months', { conversionAccuracy: 'longterm' }).months)) +
      (start.day > dateToCheck.day ? 1 : 0);
  }
  /* --- end user code --- */
}
